import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { FILE_DOWNLOAD_URL } from '@/constant/apiList';
import { useSnackbar } from '@/hooks/global-tools';
import useFileUpload, {
  AttachmentUploadError,
} from '@/hooks/approval/useFileUpload';
import { useSSE } from '@/hooks/sse/useSSE';
import useFileUploadStatus from '@/hooks/useFileUploadStatus';
import { MyInfoState } from '@/recoil/atom';
import type { DownLoadResponse } from '@/types/api';
import type {
  AttachmentUploadResult,
  AttachmentUploadVariant,
} from '@/types/approval';
import { fetchApi } from '@/utils/api/customAxios';

/** 분석 서버(approval AI)가 완료·실패를 알리는 SSE 이벤트 — 문서 유형과 무관하게 공통 */
const EXTRACT_COMPLETE_EVENT = 'approval.export.extract.complete';
const EXTRACT_ERROR_EVENT = 'approval.export.extract.error';
const SSE_MAX_RETRY = 3;
const SSE_RETRY_DELAY_MS = 2000;
// 요청 직후엔 서버에 결과 기록이 없어 조회가 404 — 이 시간 안에 열린 첫 연결은 재조회 생략
const OPEN_CATCH_UP_DELAY_MS = 5000;
// 동시 분석 중 실패 이벤트를 받은 뒤 이 시간 안에 결과가 안 오면 실패로 끝냄(멈춤 방지)
export const ERROR_SETTLE_TIMEOUT_MS = 60000;

/** 앱 전체에서 결과를 기다리는 분석 — 실패 이벤트가 누구 것인지 가릴 때 씀 */
const activePipelineIds = new Set<string>();

export const getApprovalExportTopic = (passportId: string) =>
  `approval-export:${passportId}`;

export type AiExtractionFile = { url: string; mimeType: string };

export type UseAiFileExtractionOptions<TResult> = {
  /** 분석 요청 POST 경로 — body `{ files }`, 응답 `data.id` */
  requestUrl: string;
  /** 결과 조회 GET 경로 */
  getResultUrl: (aiPipelineId: string) => string;
  /**
   * 분석 요청 body 의 files — 미지정이면 다운로드 URL `{ url, mimeType }`.
   * V2 ai-draft-source 처럼 업로드 uuid 를 받는 경로는 직접 만듦(다운로드 URL 조회 생략)
   */
  buildFiles?: (uploaded: AttachmentUploadResult, file: File) => unknown[];
  /** 분석 요청 body 에 files 와 함께 실을 값(예: 204 `kind`) */
  requestBody?: Record<string, unknown>;
  /** 업로드 형식·용량 규칙. 기본 'ai' */
  uploadVariant?: Extract<
    AttachmentUploadVariant,
    'ai' | 'ai-report' | 'ai-verifier'
  >;
  /** SSE payload.documentType — 같은 토픽을 쓰는 다른 분석과 구분. V2 ai-draft-source 는 양식 코드(숫자)로 옴 */
  documentType: string | number;
  /**
   * 결과 조회 응답 `data` → 완료 판정. 아직 진행 중이면 null, 실패면 { error } 반환.
   * 미지정이면 `data.result` 가 비어있지 않은 배열/객체일 때 완료
   */
  parseResult?: (
    data: unknown,
  ) => { result: TResult } | { error?: string } | null;
  /** 업로드 시작 / 종료(성공·실패·취소) — 업로드 중 표시용 */
  onUploadStart?: (file: File) => void;
  onUploadSettled?: (file: File) => void;
  /** 업로드 성공 직후 — 품의 첨부 목록 반영 등 */
  onUploaded?: (result: AttachmentUploadResult, file: File) => void;
  onResult: (result: TResult, aiPipelineId: string) => void;
  /** 업로드·분석 진행 여부가 바뀔 때 */
  onProcessingChange?: (processing: boolean) => void;
};

type Pending = {
  aiPipelineId: string;
  passportId: string;
  requestedAt: number;
};

const defaultParseResult = <TResult>(
  data: unknown,
): { result: TResult } | null => {
  const raw = (data as { result?: unknown } | null)?.result;
  if (Array.isArray(raw)) return raw.length ? { result: raw as TResult } : null;
  if (raw && typeof raw === 'object' && Object.keys(raw).length) {
    return { result: raw as TResult };
  }
  return null;
};

/**
 * AI 파일 분석 — 업로드 → 다운로드 URL → 분석 요청 → SSE(approval-export:{passportId}) 대기 → 결과 조회.
 * 구독 전에 끝난 분석은 연결 직후 재조회로 회수(이벤트 유실 대비, 요청 직후 첫 연결은 제외). 결과는 작업당 한 번만 반영, 취소 후 도착한 결과는 버림
 */
const useAiFileExtraction = <TResult = unknown>({
  requestUrl,
  getResultUrl,
  buildFiles,
  requestBody,
  uploadVariant = 'ai',
  documentType,
  parseResult,
  onUploadStart,
  onUploadSettled,
  onUploaded,
  onResult,
  onProcessingChange,
}: UseAiFileExtractionOptions<TResult>) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const myPassportId = useAtomValue(MyInfoState)?.passportId;
  const { upload, progress } = useFileUpload(uploadVariant);
  const { addUploading, setSuccess, setError } = useFileUploadStatus();

  const [isProcessing, setIsProcessing] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  // 취소·재시작마다 증가 — 늦게 도착한 비동기 응답이 현재 작업인지 판정
  const runRef = useRef(0);
  // 결과를 반영(또는 실패 확정)한 작업 — 연결 직후 조회와 complete 조회가 겹쳐도 한 번만 반영
  const settledRunRef = useRef(0);

  const callbacksRef = useRef({
    buildFiles,
    requestBody,
    parseResult,
    onUploadStart,
    onUploadSettled,
    onUploaded,
    onResult,
    onProcessingChange,
  });
  callbacksRef.current = {
    buildFiles,
    requestBody,
    parseResult,
    onUploadStart,
    onUploadSettled,
    onUploaded,
    onResult,
    onProcessingChange,
  };

  useEffect(() => {
    callbacksRef.current.onProcessingChange?.(isProcessing);
  }, [isProcessing]);

  const showFailure = useCallback(
    (message?: string) => {
      snackbar.showError({
        message: `[${t('AI 정리 도우미')}] ${
          message || t('파일 분석에 실패했습니다. 다시 시도해 주세요.')
        }`,
      });
    },
    [snackbar, t],
  );

  const errorTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (!pending) return undefined;
    const { aiPipelineId } = pending;
    activePipelineIds.add(aiPipelineId);
    return () => {
      activePipelineIds.delete(aiPipelineId);
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = undefined;
    };
  }, [pending]);

  const finish = useCallback(() => {
    settledRunRef.current = runRef.current;
    setPending(null);
    setIsProcessing(false);
  }, []);

  const fetchResult = useCallback(
    async (
      target: Pending,
      run: number,
      settleOnEmpty: boolean,
      // 결과 조회가 실패하면(기록 없음 등) 결과가 비어 있어도 실패로 끝냄
      settleOnRequestError = settleOnEmpty,
    ) => {
      try {
        const res = await fetchApi<{ data?: unknown }>({
          method: 'GET',
          url: getResultUrl(target.aiPipelineId),
        });
        if (run !== runRef.current || settledRunRef.current === run) return;
        const parse = callbacksRef.current.parseResult ?? defaultParseResult;
        const parsed = parse(res?.data ?? null);
        if (parsed && 'result' in parsed) {
          finish();
          callbacksRef.current.onResult(
            parsed.result as TResult,
            target.aiPipelineId,
          );
          return;
        }
        if (parsed && 'error' in parsed) {
          finish();
          showFailure(parsed.error);
          return;
        }
        if (settleOnEmpty) {
          finish();
          showFailure();
        }
      } catch {
        if (
          run !== runRef.current ||
          settledRunRef.current === run ||
          !settleOnRequestError
        ) {
          return;
        }
        finish();
        showFailure();
      }
    },
    [finish, getResultUrl, showFailure],
  );

  const start = useCallback(
    async (file: File) => {
      runRef.current += 1;
      const run = runRef.current;
      setPending(null);
      setIsProcessing(true);

      const statusId = addUploading(file.name, file);
      callbacksRef.current.onUploadStart?.(file);
      let uploaded: AttachmentUploadResult;
      try {
        uploaded = await upload(file);
      } catch (err) {
        // 업로드 상태·임시 항목은 취소 여부와 무관하게 이 파일 기준으로 종료
        callbacksRef.current.onUploadSettled?.(file);
        setError(
          statusId,
          err instanceof AttachmentUploadError && err.i18n
            ? t(err.i18n.key, err.i18n.options)
            : t('파일 업로드에 실패했습니다. 다시 시도해 주세요.'),
        );
        if (run === runRef.current) setIsProcessing(false);
        return;
      }
      // 분석을 취소해도 업로드는 끝까지 — 첨부로 남기고 분석만 생략(레거시 동일)
      setSuccess(statusId);
      callbacksRef.current.onUploadSettled?.(file);
      callbacksRef.current.onUploaded?.(uploaded, file);
      if (run !== runRef.current) return;

      try {
        let files: unknown[];
        const customBuildFiles = callbacksRef.current.buildFiles;
        if (customBuildFiles) {
          files = customBuildFiles(uploaded, file);
        } else {
          const download = await fetchApi<{ data?: DownLoadResponse }>({
            method: 'GET',
            url: `${FILE_DOWNLOAD_URL}?uuid=${uploaded.uuid}`,
          });
          const url = download?.data?.url;
          if (!url) throw new Error('download url missing');
          files = [{ url, mimeType: file.type }] satisfies AiExtractionFile[];
        }
        const res = await fetchApi<{
          data?:
            | { id?: string; aiPipelineId?: string; passportId?: string }
            | string;
        }>({
          method: 'POST',
          url: requestUrl,
          data: { ...callbacksRef.current.requestBody, files },
        });
        if (run !== runRef.current) return;

        const data = res?.data;
        const aiPipelineId =
          typeof data === 'string' ? data : data?.aiPipelineId ?? data?.id;
        const passportId =
          (typeof data === 'object' && data?.passportId) || myPassportId;
        if (!aiPipelineId || !passportId) {
          setIsProcessing(false);
          showFailure(
            `${t('파일 분석에 실패했습니다. 다시 시도해 주세요.')} ${t(
              '(ID 생성 오류)',
            )}`,
          );
          return;
        }
        setPending({ aiPipelineId, passportId, requestedAt: Date.now() });
      } catch {
        if (run !== runRef.current) return;
        setIsProcessing(false);
        showFailure(
          `${t('파일 분석에 실패했습니다. 다시 시도해 주세요.')} ${t(
            '(결과 표시 오류)',
          )}`,
        );
      }
    },
    [
      addUploading,
      myPassportId,
      requestUrl,
      setError,
      setSuccess,
      showFailure,
      t,
      upload,
    ],
  );

  const cancel = useCallback(() => {
    runRef.current += 1;
    finish();
    snackbar.showSuccess({ message: t('AI 분석이 취소되었습니다.') });
  }, [finish, snackbar, t]);

  const topics = useMemo(
    () => (pending ? [getApprovalExportTopic(pending.passportId)] : []),
    [pending],
  );

  const handlers = useMemo(() => {
    const isTarget = (data: {
      documentType?: string | number;
      aiPipelineId?: string;
      payload?: { documentType?: string | number; aiPipelineId?: string };
    }) =>
      String(data?.payload?.documentType ?? data?.documentType) ===
        String(documentType) &&
      (data?.payload?.aiPipelineId ?? data?.aiPipelineId) ===
        pending?.aiPipelineId;
    return {
      [EXTRACT_COMPLETE_EVENT]: (data: Parameters<typeof isTarget>[0]) => {
        if (!pending || !isTarget(data)) return;
        fetchResult(pending, runRef.current, true);
      },
      // 실측상 aiPipelineId 가 요청 id 와 다르게 와서 대상 판정 불가 — 혼자면 바로 종료(레거시 동일),
      // 분석이 여럿 진행 중이면(204 신분증·통장) 다른 쪽 실패로 끝나지 않게 각자 결과를 다시 봄
      [EXTRACT_ERROR_EVENT]: () => {
        if (!pending) return;
        if (activePipelineIds.size > 1) {
          const run = runRef.current;
          fetchResult(pending, run, false, true);
          // 아직 진행 중이면 완료 이벤트를 기다리되, 끝내 안 오면 실패로 끝냄 — 이벤트가 반복돼도 첫 실패 기준
          if (!errorTimerRef.current) {
            errorTimerRef.current = setTimeout(
              () => fetchResult(pending, run, true),
              ERROR_SETTLE_TIMEOUT_MS,
            );
          }
          return;
        }
        finish();
        showFailure();
      },
    };
  }, [documentType, fetchResult, finish, pending, showFailure]);

  // 연결이 끊기면 2초 뒤 최대 3회 재연결, 그래도 안 되면 요청 과다 안내(레거시 동일)
  const retryCountRef = useRef(0);
  const [reconnecting, setReconnecting] = useState(false);
  useEffect(() => {
    retryCountRef.current = 0;
  }, [pending]);
  useEffect(() => {
    if (!reconnecting) return undefined;
    const timer = setTimeout(() => setReconnecting(false), SSE_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [reconnecting]);

  useSSE(topics, handlers, topics.length > 0 && !reconnecting, {
    onOpen: () => {
      if (!pending) return;
      if (
        retryCountRef.current === 0 &&
        Date.now() - pending.requestedAt < OPEN_CATCH_UP_DELAY_MS
      ) {
        return;
      }
      fetchResult(pending, runRef.current, false);
    },
    onDisconnect: () => {
      if (!pending) return;
      if (retryCountRef.current < SSE_MAX_RETRY) {
        retryCountRef.current += 1;
        setReconnecting(true);
        return;
      }
      finish();
      snackbar.showError({
        message: t(
          '접수된 요청이 많아 AI 분석 결과를 생성할 수 없습니다. 잠시 후 다시 시도해 주세요.',
        ),
      });
    },
  });

  return { start, cancel, isProcessing, progress };
};

export default useAiFileExtraction;
